import { useCallback, useEffect, useRef, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import { createTollEntries, editTolls, getTolls } from "../../services/tolls";
import {
  buildTollBaselineCellMap,
  buildTollPathDateMap,
  buildTollSavePlan,
  buildTollTableSections,
  getModifiedCellsAfterRowUpdate,
  normalizeTollRow,
} from "../../utils/tolls";

const useTolls = () => {
  const [tableSections, setTableSections] = useState([]);
  const [modifiedCells, setModifiedCells] = useState(new Map());
  const [pathDateMap, setPathDateMap] = useState(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState(moment().format("YYYY"));
  const baselineCellMapRef = useRef(new Map());

  const loadTolls = useCallback(async (year) => {
    setIsLoading(true);
    setModifiedCells(new Map());

    try {
      const data = await getTolls(year);

      const currentPathDateMap = buildTollPathDateMap(data);
      const nextTableSections = buildTollTableSections(data);

      baselineCellMapRef.current = buildTollBaselineCellMap(nextTableSections);
      setPathDateMap(currentPathDateMap);
      setTableSections(nextTableSections);
    } catch (error) {
      toast.error("Error al cargar los peajes");
      baselineCellMapRef.current = new Map();
      setTableSections([]);
      setPathDateMap(new Map());
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTolls(selectedYear);
  }, [loadTolls, selectedYear]);

  const processRowUpdate = (updatedRow, _originalRow, tableKey) => {
    const normalizedRow = normalizeTollRow(updatedRow);

    setTableSections((prevSections) =>
      prevSections.map((section) =>
        section.key !== tableKey
          ? section
          : {
              ...section,
              rows: section.rows.map((row) =>
                row.id === normalizedRow.id ? normalizedRow : row
              ),
            }
      )
    );

    setModifiedCells((prevModifiedCells) =>
      getModifiedCellsAfterRowUpdate({
        previousModifiedCells: prevModifiedCells,
        updatedRow: normalizedRow,
        baselineCellMap: baselineCellMapRef.current,
      })
    );

    return normalizedRow;
  };

  const handleSaveChanges = async () => {
    const modifiedCellCount = modifiedCells.size;
    const { invalidValues, toCreate, toUpdate } = buildTollSavePlan({
      modifiedCells,
      pathDateMap,
      selectedYear,
    });

    if (invalidValues.length > 0) {
      toast.error(
        `Valores de peaje inválidos (deben ser numéricos y >= 0): ${invalidValues.join(", ")}`
      );
      return;
    }

    if (toUpdate.length === 0 && toCreate.length === 0) {
      toast.error("No hay cambios válidos para guardar");
      return;
    }

    try {
      if (toCreate.length > 0) await createTollEntries(toCreate);
      if (toUpdate.length > 0) await editTolls(toUpdate);
      await loadTolls(selectedYear);

      toast.success(
        `Cambios guardados exitosamente (${modifiedCellCount} celdas modificadas)`
      );
    } catch (error) {
      toast.error("Error al guardar los cambios de peajes");
    }
  };

  return {
    tableSections,
    modifiedCells,
    selectedYear,
    setSelectedYear,
    processRowUpdate,
    handleSaveChanges,
    isLoading,
  };
};

export default useTolls;
